/**
 * Wrong spellings, generated from rules.
 *
 * For the exercise “How do you write that?” a third spelling is needed
 * besides the correct one and the one Elmar wrote down — otherwise guessing
 * hits in half of the cases.
 *
 * The third one is NOT invented but formed from the same rule he failed at.
 * Whoever forgot the silent h in “fare” gets “faare” next to it: also wrong,
 * and wrong in the same way. That trains looking closely instead of ruling
 * out nonsense.
 *
 * Everything here is pure string work and can be checked without a browser.
 */

import type { MistakeKind } from '../types.js'
import { locateError } from '../spell/dictionary.js'

/**
 * Where a mistake comes from. That decides which variants are fit for it.
 *
 * `photo` — from a page taken with the camera. Here mistakes are possible
 *           that hang on the handwriting: “Haus arrest” comes about because a
 *           gap is read where none belongs.
 * `typed` — from a text he wrote himself. Nobody types a gap in the middle of
 *           a word; such variants would not be wrong here but impossible.
 */
export type Source = 'photo' | 'typed'

/**
 * Forms variants of a word according to the kind of mistake.
 *
 * @param right   The correct spelling — it is the one that gets modified.
 * @param kind    The kind of mistake that the model assigned.
 * @param source  Where the mistake comes from. Some variants exist only when
 *                copying from paper.
 * @returns Wrong spellings, without `right` itself. Can be empty.
 */
export function variants(
  right: string,
  kind: MistakeKind | string,
  source: Source = 'photo'
): string[] {
  if (source === 'typed' && HANDWRITING_ONLY.has(kind)) return []
  const f = RULES[kind]
  if (!f) return []
  // Duplicates and the correct word itself fly out: a set of choices in which
  // the solution stands twice has no wrong answer any more.
  return [...new Set(f(right))].filter((v) => v && v !== right)
}

/**
 * Kinds of mistake that exist only on paper.
 *
 * Apart or together is a conscious decision when typing — nobody writes “Haus
 * arrest” with the space bar in the middle of the word. On paper, by
 * contrast, it comes about on its own: the handwriting sets a gap, and
 * whoever reads it sees two words. To put such a variant before a typed
 * mistake would be no exercise but an oddity.
 */
const HANDWRITING_ONLY = new Set(['getrennt-zusammen'])

type Rule = (w: string) => string[]

const RULES: Record<string, Rule> = {
  /**
   * The silent h. Either it is missing, or there is one too many.
   *
   * Both directions, because both occur: “fare” instead of “fahre”, but also
   * “mahlen” instead of “malen”.
   *
   * An h only goes in where it COULD also stand: after a vowel that is
   * followed by another letter. An h at the end of a word (“fahreh”) is
   * something else — on that see `inSentenceOnly` further below.
   */
  'dehnungs-h': (w) => [
    w.replace(/([aeiouäöü])h/i, '$1'),
    // Only if there is no h yet: otherwise it would stand there twice.
    /[aeiouäöü]h/i.test(w) ? '' : w.replace(/([aeiouäöü])(?=[bdflmnrt])/i, '$1h'),
  ],

  /**
   * Doubled consonant. “schwimt” instead of “schwimmt”, “komt” instead of
   * “kommt”.
   */
  doppelkonsonant: (w) => [
    w.replace(/([bcdfgklmnprstz])\1/i, '$1'),
    w.replace(/([aeiouäöü])([bcdfgklmnprstz])(?!\2)/i, '$1$2$2'),
  ],

  /** ie or i. “schif” instead of “Schiff”, “wiedersteht” instead of “widersteht”. */
  'ie-i': (w) => [w.replace(/ie/i, 'i'), w.replace(/i(?!e)/i, 'ie')],

  /**
   * s, ss or ß. All three against one another — which one is right depends on
   * the vowel before it, and that is exactly what the exercise is to show.
   */
  's-ss-ß': (w) => [
    w.replace(/ß/, 'ss'),
    w.replace(/ss/i, 'ß'),
    w.replace(/ß/, 's'),
    w.replace(/ss/i, 's'),
  ],

  /** d or t at the end. “ant” instead of “and”, “ent” instead of “end”. */
  'd-t': (w) => [
    w.replace(/d$/i, 't'),
    w.replace(/t$/i, 'd'),
    w.replace(/d(?=[^aeiouäöü]*$)/i, 't'),
  ],

  /** Missing dots. “Nordlich” instead of “Nördlich”. */
  umlaut: (w) => [
    w.replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u'),
    w.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue'),
  ],

  /**
   * Missing final letter. “is” instead of “ist”, “de” instead of “der”.
   *
   * Only leave out, do not double: “istt” is not a mistake a child makes but
   * a typing slip. If the third possibility is missing because of that, it
   * stays at two — better than one that gives itself away.
   *
   * And only where a letter CAN fall away unheard. Cutting blindly produced
   * “Gra” next to “Gras” — not a spelling anybody would weigh up, but a
   * word broken off. What falls away is the ending one does not hear: the t
   * of “ist”, the r of “der”, the n of “lachen”. A consonant that carries
   * the sound of the word stays.
   */
  endung: (w) => {
    const last = w.slice(-1).toLowerCase()
    // These are the endings that go missing in writing because they are
    // barely spoken. An s is not among them — it hisses, and a child hears
    // it.
    if (!'tnrem'.includes(last)) return []
    const shorter = w.slice(0, -1)
    // Two letters are enough — “is” and “de” are the very mistakes this rule
    // is for. A single letter is not a spelling but a fragment.
    return shorter.length >= 2 ? [shorter] : []
  },

  /**
   * Upper or lower case.
   *
   * Stands here only for the sake of completeness — for that there is the
   * tapping exercise of its own that shows the whole sentence. To choose a
   * word upper or lower case on its own, without seeing where it stands in
   * the sentence, is not a sensible question.
   */
  'gross-klein': (w) => {
    const c = w[0]
    if (!c) return []
    return [
      c === c.toUpperCase()
        ? c.toLowerCase() + w.slice(1)
        : c.toUpperCase() + w.slice(1),
    ]
  },

  /**
   * Apart or together. “Haus arrest” instead of “Hausarrest”.
   *
   * The place of the split is not guessed: if there is no gap in the correct
   * word, the split is made in the middle — as a WRONG spelling that is just
   * right, because wrongly split is wrongly split.
   */
  'getrennt-zusammen': (w) => {
    if (w.includes(' ')) return [w.replace(/\s+/g, '')]
    // Split at a joint, not just anywhere: “Hausa rrest” is obviously
    // nonsense, “Haus arrest” is the mistake at issue. A joint lies where a
    // vowel could begin a new word after a consonant — both parts at least
    // three letters long.
    //
    // If no such place is found, NO variant comes out. Better two
    // possibilities than a third that gives itself away.
    for (let i = 3; i <= w.length - 3; i++) {
      const before = w[i - 1] ?? ''
      const at = w[i] ?? ''
      if (/[^aeiouäöü\s]/i.test(before) && /[aeiouäöü]/i.test(at)) {
        return [w.slice(0, i) + ' ' + w.slice(i)]
      }
    }
    return []
  },
}

/**
 * Variants that only become a question IN THE SENTENCE.
 *
 * “fahreh” on its own next to “fahre” is no exercise — one sees immediately
 * that the h at the back does not belong there, without thinking about the
 * rule. In the sentence, by contrast, it is one: “Ich fahreh mit dem Rad”
 * reads through when skimming quickly, and exactly this quick skimming is the
 * problem with one's own text.
 *
 * That is why these forms stand here separately. They are only used in
 * exercises that show the sentence along with it.
 */
export function inSentenceOnly(right: string, kind: MistakeKind | string): string[] {
  if (kind === 'dehnungs-h') {
    // The h at the end — but only after a vowel (“fahre” → “fahreh”). After a
    // consonant “Strahlungh” would come out, and that is not a spelling that
    // anyone holds to be possible, in a sentence either.
    if (/[aeiouäöü]$/i.test(right)) return [right + 'h']
  }
  return []
}

/**
 * The choices for an exercise: right, his spelling, a third one.
 *
 * Shuffled, but not at random per call — otherwise the buttons jump on every
 * render. The mistake key determines the order, so it stays the same as long
 * as the same exercise stands there.
 */
export function choices(
  right: string,
  wrong: string,
  kind: MistakeKind | string,
  seed: string,
  source: Source = 'photo',
  /** Is the sentence shown along with it? Then more variants make sense. */
  withSentence = false
): string[] {
  // Two possibilities that differ only in capitalisation stand next to each
  // other as two almost identical buttons — “Stralung” and “stralung”. That
  // is no longer a question about spelling but an eye test. The comparison is
  // therefore made without regard to upper and lower case.
  //
  // UNLESS capitalisation is the question. Then those two are the whole
  // exercise, and folding them together leaves a single button: “Zoo”
  // against nothing. The rule holds for every other kind of mistake, where
  // the capital is a side effect and not the point.
  const caseIsTheQuestion = kind === 'gross-klein'
  const same = (a: string, b: string): boolean =>
    caseIsTheQuestion ? a === b : a.toLowerCase() === b.toLowerCase()

  const third = [...variants(right, kind, source), ...(withSentence ? inSentenceOnly(right, kind) : [])]
    .filter((v) => !same(v, wrong) && !same(v, right))

  const all = [right, wrong, ...third.slice(0, 1)]
  // Two possibilities are better than three of which one is nonsense: if no
  // rule applies, it stays at right against his spelling.
  const withoutDuplicates: string[] = []
  for (const w of all) {
    if (!withoutDuplicates.some((x) => same(x, w))) withoutDuplicates.push(w)
  }
  return shuffle(withoutDuplicates, seed)
}

/**
 * Three spellings, one of which is not a word — for „welches gibt es nicht?"
 *
 * The class test had this the other way round from „wie schreibt man das?":
 * reisen, reißen, reissen — two of them are words with different meanings,
 * one of them is nothing. It takes the rule AND the vocabulary, and it is
 * harder for it.
 *
 * The candidates are the same as everywhere here — the right spelling, his,
 * and the rule's variants of the right one. The DICTIONARY says which of
 * them are words. Only when two are and one is not does the exercise exist;
 * it is not invented when the set does not come out. „Maß" against „Mass"
 * and „Mas" gives nothing: only one of the three is a word, and that is the
 * other exercise.
 *
 * Preferring his: when his spelling is the non-word, it is the answer — that
 * is the mistake he made. When his spelling IS a word (he wrote „reisen" for
 * „reißen"), it stands among the two that exist.
 *
 * @param exists  The dictionary's verdict on a word.
 * @returns the three options, shuffled by `seed`, and which one is nothing.
 */
export function nonWord(
  right: string,
  wrong: string,
  kind: MistakeKind | string,
  seed: string,
  source: Source,
  exists: (w: string) => boolean
): { options: string[]; missing: string } | null {
  if (right.includes(' ') || wrong.includes(' ')) return null
  const same = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase()
  const candidates: string[] = []
  for (const w of [right, wrong, ...variants(right, kind, source)]) {
    if (w && !candidates.some((x) => same(x, w))) candidates.push(w)
  }
  const real = candidates.filter((w) => exists(w))
  const nothing = candidates.filter((w) => !exists(w))
  if (real.length < 2 || !nothing.length) return null
  // The right spelling must be among the words — a dictionary that does not
  // know the right spelling has nothing to say about the rest.
  if (!real.some((w) => same(w, right))) return null

  const missing = nothing.find((w) => same(w, wrong)) ?? nothing[0]!
  const others = [right, ...real.filter((w) => !same(w, right))].slice(0, 2)
  return { options: shuffle([...others, missing], seed), missing }
}

/**
 * Shuffles by a key — same input, same order.
 *
 * A `Math.random()` at render time would swap the buttons under the finger as
 * soon as anything else redraws the view.
 */
export function shuffle<T>(list: T[], seed: string): T[] {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    h = Math.imul(h ^ (h >>> 15), 2246822507)
    const j = Math.abs(h) % (i + 1)
    const a = out[i]!
    const b = out[j]!
    out[i] = b
    out[j] = a
  }
  return out
}

/**
 * Splits the word into a stem and the piece the mistake sits in.
 *
 * The exercise “setz das Wort zusammen” was tried as a jigsaw first: all the
 * parts shuffled, tap them into order. It practised nothing. With two pieces
 * there are only two orders, and the wrong one reads “lungStrah” — nobody
 * has to know a rule to rule that out.
 *
 * So the word is not scrambled but OPENED at the place of the mistake: the
 * stem stands there, and the ending is chosen from two or three spellings.
 * “Strah…” plus “lung”, and next to it the same syllable without the h. That
 * is the rule, and nothing else.
 *
 * @param right   The correct spelling.
 * @param wrong   His spelling — it says where the mistake sits.
 * @returns `{ stem, ending }` where `stem + ending === right`, or null when
 *          the word is unfit: too short, or the place lies at the very edge.
 */
export function opened(right: string, wrong: string): { stem: string; ending: string } | null {
  if (right.length < 5) return null
  if (right.includes(' ')) return null

  const { start, end } = locateError(wrong, right)
  const behind = Math.min(Math.max(end, Math.min(start, right.length) + 1), right.length)

  // The cut goes BEFORE the place of the mistake, so that the letter in
  // question lies in the part that is chosen. Moved back to the syllable
  // joint, so that the stem can be read out loud: "Strah|lung" would put the
  // h into the stem and give it away — "Stra|hlung" is unreadable. What is
  // wanted is the joint in front of the mistake.
  let cut = syllableCut(right, Math.max(2, start))
  // Never behind the place of the mistake: then the letter would stand in
  // the stem and the choice would be about something else.
  if (cut > start && start >= 2) cut = start
  if (cut < 2) cut = 2
  if (cut > right.length - 2) cut = right.length - 2
  if (cut < 2 || cut >= right.length - 1) return null

  return { stem: right.slice(0, cut), ending: right.slice(cut) }
}

function syllableCut(word: string, from: number): number {
  const vowel = (c: string): boolean => /[aeiouäöüy]/i.test(c)

  // At most four letters of search: further away the cut no longer has
  // anything to do with the place of the mistake.
  const limit = Math.min(word.length - 2, from + 4)
  for (let i = Math.max(2, from); i <= limit; i++) {
    // The joint lies BEFORE the consonant that starts the next syllable:
    // "Strah|lung", not "Strahl|ung". So at `i` a consonant must stand, a
    // vowel behind it, and a vowel in front — otherwise the first part
    // cannot be spoken.
    const here = word[i] ?? ''
    const next = word[i + 1] ?? ''
    if (!vowel(here) && vowel(next) && /[aeiouäöüy]/i.test(word.slice(0, i))) {
      return i
    }
  }
  return from
}

/**
 * The endings to choose from — the right one and one or two wrong ones.
 *
 * Built out of the WHOLE word and then cut back to the ending, not formed on
 * the ending alone: the rules in `RULES` look for a vowel in front of the
 * doubled letter, and a fragment like “mt” does not carry one. “schwimmt”
 * gives “schwimt”, and from that the ending “mt” against “t”.
 *
 * His own spelling comes first among the wrong ones — that is the mistake he
 * really made.
 *
 * @returns Two or three endings, shuffled by `seed`. Empty when nothing
 *          usable came out; the caller then leaves the exercise out.
 */
export function endings(
  right: string,
  wrong: string,
  kind: MistakeKind | string,
  seed: string,
  source: Source = 'photo'
): string[] {
  const split = opened(right, wrong)
  if (!split) return []
  const { stem, ending } = split

  const tail = (w: string): string | null => {
    // Only spellings that keep the stem: a variant that changes something in
    // front of the cut cannot be shown as an ending.
    if (!w.toLowerCase().startsWith(stem.toLowerCase())) return null
    const t = w.slice(stem.length)
    return t && t.toLowerCase() !== ending.toLowerCase() ? t : null
  }

  const wrongOnes: string[] = []
  // His own spelling first, if it fits the stem.
  const his = tail(wrong)
  if (his) wrongOnes.push(his)
  for (const v of variants(right, kind, source)) {
    const t = tail(v)
    if (t && !wrongOnes.some((x) => x.toLowerCase() === t.toLowerCase())) wrongOnes.push(t)
  }

  if (!wrongOnes.length) return []

  // An ending only counts when it sits at the same place.
  //
  // The wrong spellings here are all SHORTER or as long: a letter is
  // missing, a doubling is not made. An ending that is LONGER comes from a
  // rule that inserts something somewhere else — the ie/i rule turns
  // "Schiff" into "Schieff" and would offer "eff" against "ff", a different
  // place and a possibility nobody weighs up.
  //
  // One letter longer is still allowed: that is the doubling too many
  // ("faare"), a mistake that really happens.
  const close = wrongOnes.filter((t) => {
    if (t.length > ending.length + 1) return false
    // The tell-tale case: same length or longer AND a different first
    // letter means the variant starts somewhere else.
    if (t.length >= ending.length && t[0]?.toLowerCase() !== ending[0]?.toLowerCase()) return false
    return true
  })
  if (!close.length) return []

  return shuffle([ending, ...close.slice(0, 2)], seed)
}
